import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { config } from '../../config/config';

export const stocktakeApi = createApi({
  reducerPath: 'stocktakeApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${config.apiUrl}/stocktake`,
    prepareHeaders: (headers, { getState }) => {
      const token = getState().auth.user?.token;
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['Stocktakes', 'StocktakeBundle'],
  endpoints: (builder) => ({
    listStocktakes: builder.query({
      query: (params) => ({ url: '/stocktakes', params }),
      providesTags: [{ type: 'Stocktakes', id: 'LIST' }],
    }),
    listWarehouses: builder.query({
      query: () => '/warehouses',
    }),
    getStocktakeBundle: builder.query({
      query: (stocktakeId) => `/stocktakes/${stocktakeId}/bundle`,
      providesTags: (result, err, stocktakeId) => [{ type: 'StocktakeBundle', id: stocktakeId }],
    }),
    createLocalStocktake: builder.mutation({
      query: (warehouseId) => ({
        url: '/stocktakes',
        method: 'POST',
        body: { warehouseId },
      }),
      invalidatesTags: [{ type: 'Stocktakes', id: 'LIST' }],
    }),
    startStocktake: builder.mutation({
      query: (stocktakeId) => ({
        url: `/stocktakes/${stocktakeId}/start`,
        method: 'POST',
      }),
      invalidatesTags: (r, e, id) => [
        { type: 'Stocktakes', id: 'LIST' },
        { type: 'StocktakeBundle', id },
      ],
    }),
    completeStocktake: builder.mutation({
      query: (stocktakeId) => ({
        url: `/stocktakes/${stocktakeId}/complete`,
        method: 'POST',
      }),
      invalidatesTags: (r, e, id) => [
        { type: 'Stocktakes', id: 'LIST' },
        { type: 'StocktakeBundle', id },
      ],
    }),
    reopenStocktake: builder.mutation({
      query: (stocktakeId) => ({
        url: `/stocktakes/${stocktakeId}/reopen`,
        method: 'POST',
      }),
      invalidatesTags: (r, e, id) => [
        { type: 'Stocktakes', id: 'LIST' },
        { type: 'StocktakeBundle', id },
      ],
    }),
    scanStocktakeCode: builder.mutation({
      query: ({ stocktakeId, code, increment = true }) => ({
        url: `/stocktakes/${stocktakeId}/scan`,
        method: 'POST',
        body: { code, increment },
      }),
      invalidatesTags: (r, e, { stocktakeId }) => [{ type: 'StocktakeBundle', id: stocktakeId }],
    }),
    searchStocktakeProducts: builder.mutation({
      query: ({ stocktakeId, code }) => ({
        url: `/stocktakes/${stocktakeId}/search-products`,
        method: 'POST',
        body: { code },
      }),
    }),
    applyStocktakeProduct: builder.mutation({
      query: ({ stocktakeId, productId, increment = true }) => ({
        url: `/stocktakes/${stocktakeId}/apply-product`,
        method: 'POST',
        body: { productId, increment },
      }),
      invalidatesTags: (r, e, { stocktakeId }) => [{ type: 'StocktakeBundle', id: stocktakeId }],
    }),
    updateStocktakeLine: builder.mutation({
      query: ({ stocktakeId, lineId, quantity }) => ({
        url: `/stocktakes/${stocktakeId}/lines/${lineId}`,
        method: 'PATCH',
        body: { quantity },
      }),
      invalidatesTags: (r, e, { stocktakeId }) => [{ type: 'StocktakeBundle', id: stocktakeId }],
    }),
    addStocktakeLine: builder.mutation({
      query: ({ stocktakeId, productId, quantityActual }) => ({
        url: `/stocktakes/${stocktakeId}/lines`,
        method: 'POST',
        body: { productId, quantityActual },
      }),
      invalidatesTags: (r, e, { stocktakeId }) => [{ type: 'StocktakeBundle', id: stocktakeId }],
    }),
    markStocktakeDeleted: builder.mutation({
      query: (stocktakeId) => ({
        url: `/stocktakes/${stocktakeId}/mark-deleted`,
        method: 'POST',
      }),
      invalidatesTags: (r, e, stocktakeId) => [
        { type: 'Stocktakes', id: 'LIST' },
        { type: 'StocktakeBundle', id: stocktakeId },
      ],
    }),
    deleteStocktakeLine: builder.mutation({
      query: ({ stocktakeId, lineId }) => ({
        url: `/stocktakes/${stocktakeId}/lines/${lineId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (r, e, { stocktakeId }) => [
        { type: 'Stocktakes', id: 'LIST' },
        { type: 'StocktakeBundle', id: stocktakeId },
      ],
    }),
    getCatalogBrowse: builder.query({
      query: ({ stocktakeId, parentId, search }) => ({
        url: `/stocktakes/${stocktakeId}/catalog`,
        params: { parentId, search },
      }),
    }),
  }),
});

export const {
  useListStocktakesQuery,
  useListWarehousesQuery,
  useGetStocktakeBundleQuery,
  useCreateLocalStocktakeMutation,
  useStartStocktakeMutation,
  useCompleteStocktakeMutation,
  useReopenStocktakeMutation,
  useScanStocktakeCodeMutation,
  useSearchStocktakeProductsMutation,
  useApplyStocktakeProductMutation,
  useUpdateStocktakeLineMutation,
  useAddStocktakeLineMutation,
  useMarkStocktakeDeletedMutation,
  useDeleteStocktakeLineMutation,
  useGetCatalogBrowseQuery,
} = stocktakeApi;
